import { BILLING_STATUS_QUERY_KEY } from "@/services/api/billingService"
import { USER_LIMITS_QUERY_KEY } from "@/services/api/userService"
import { triggerFlagRefresh } from "@/utils/featureFlagStore"
import {
  SESSION_REFRESHED_EVENT,
  refreshSessionIfUpgradePending,
} from "@/utils/upgradeSession"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"

/** Longer than the backend's flag cache (feature_flag_service.py, 30 seconds,
 *  per task): the purchase opens the team just after the group reaches the token. */
const FLAG_RELOAD_DELAY_MS = 35_000

// What a changed plan changes: the groups on the user, the trial, the limits and
// what the upgrade page offers.
const ACCOUNT_QUERY_KEYS = [
  ["userData"],
  ["userFreeTrialsLeftData"],
  USER_LIMITS_QUERY_KEY,
  BILLING_STATUS_QUERY_KEY,
] as const

/**
 * Keeps the session in step with an upgrade made in the in-app checkout, which
 * marks the upgrade pending when it creates a checkout session. On return (tab
 * switch, focus or a reload in this tab) it forces a token refresh so the paid
 * group arrives early, then refetches the account.
 */
export function useUpgradeSessionSync(): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    let flagReload: ReturnType<typeof setTimeout> | undefined
    const refreshOnReturn = () => {
      if (document.visibilityState === "visible") {
        void refreshSessionIfUpgradePending()
      }
    }
    const refetchAccount = () => {
      for (const queryKey of ACCOUNT_QUERY_KEYS) {
        void queryClient.invalidateQueries({ queryKey })
      }
      triggerFlagRefresh()
      clearTimeout(flagReload)
      flagReload = setTimeout(triggerFlagRefresh, FLAG_RELOAD_DELAY_MS)
    }

    // A tab switch fires visibilitychange but not focus; another window, the reverse.
    window.addEventListener("focus", refreshOnReturn)
    document.addEventListener("visibilitychange", refreshOnReturn)
    window.addEventListener(SESSION_REFRESHED_EVENT, refetchAccount)
    // The checkout was left in this very tab and the app has loaded again.
    refreshOnReturn()

    return () => {
      window.removeEventListener("focus", refreshOnReturn)
      document.removeEventListener("visibilitychange", refreshOnReturn)
      window.removeEventListener(SESSION_REFRESHED_EVENT, refetchAccount)
      clearTimeout(flagReload)
    }
  }, [queryClient])
}
